import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ProjectIcon from '@mui/icons-material/Work';
import TaskIcon from '@mui/icons-material/Assignment';
import SettingsIcon from '@mui/icons-material/Settings';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import LogoutIcon from '@mui/icons-material/Logout';
import ChartAnalyticsIcon from '@mui/icons-material/InsertChart';
import RuleIcon from '@mui/icons-material/Rule';
import FlagIcon from '@mui/icons-material/Flag';
import { auth } from '../auth/firebaseConfig';
import { signOut } from 'firebase/auth';
import { styled } from '@mui/material/styles';
import { fetchUserDetails } from '../../DatabaseApis/DBQuery';
import BarChartIcon from '@mui/icons-material/BarChart';
import { getCookie } from '../../utils/cookieUtils';

const Logo = styled('img')({
  display: 'block',
  objectFit: 'contain',
});

const SideDrawer = ({ projectId }) => {
  const [open, setOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { 
    const userData = getCookie('userData');

    if (userData?.token) {
      setIsAuthenticated(true);

      const getUserDetails = async () => {
        try {
          const data = await fetchUserDetails(userData.token);
          const role = (data.role || '').toLowerCase();
          setIsAdmin(role === 'admin' || role === 'tenant admin');
          setIsSuperAdmin(role === 'superadmin');
        } catch (error) {
          console.error('Error fetching user details:', error);
        }
      };

      getUserDetails();
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      sessionStorage.clear();
      localStorage.clear();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const isProjectPage = location.pathname === '/project-page';
  const isExpanded = open || hoverOpen;

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      onMouseEnter={() => setHoverOpen(true)}
      onMouseLeave={() => setHoverOpen(false)}
      sx={{
        width: isExpanded ? 240 : 64,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isExpanded ? 240 : 64,
          transition: 'width 0.3s',
          overflowX: 'hidden',
        },
      }}
    >
      <Link to="/project-page" style={{ textDecoration: 'none' }}>
        <Logo
          src="/logo.png"
          alt="OV Plan Logo"
          sx={{
            height: isExpanded ? 90 : 72,
            width: isExpanded ? 150 : 56,
            maxWidth: '100%',
            margin: isExpanded ? '14px auto 10px' : '12px auto 8px',
            transition: 'all 0.25s ease',
          }}
        />
      </Link>

      <List sx={{ pt: 9 }}>
        <ListItem button component={Link} to="/project-page">
          <ListItemIcon title="Select Projects">
            <ProjectIcon />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="Select Projects" />}
        </ListItem>


        <Tooltip title={projectId && !isProjectPage ? '' : 'Select project first'} arrow>
          <span>
            <ListItem
              button
              component={projectId && !isProjectPage ? Link : 'div'}
              to={projectId && !isProjectPage ? `/dashboard/${projectId}` : undefined}
              disabled={!projectId || isProjectPage}
            >
              <ListItemIcon title="Dashboard">
                <DashboardIcon />
              </ListItemIcon>
              {isExpanded && <ListItemText primary="Dashboard" />}
            </ListItem>
          </span>
        </Tooltip>

        <Tooltip title={projectId && !isProjectPage ? '' : 'Select project first'} arrow>
          <span>
            <ListItem
              button
              component={projectId && !isProjectPage ? Link : 'div'}
              to={projectId && !isProjectPage ? `/board-page/${projectId}` : undefined}
              disabled={!projectId || isProjectPage}
            >
              <ListItemIcon title="Product Board">
                <TaskIcon />
              </ListItemIcon>
              {isExpanded && <ListItemText primary="Board" />}
            </ListItem>
          </span>
        </Tooltip>

        {/*
        Original active List View link (commented out to keep code)
        <Tooltip title={projectId ? 'Go to List View' : 'Select project first'} arrow>
          <span>
            <ListItem
              button
              component={projectId ? Link : 'div'}
              to={projectId ? `/list-view/${projectId}` : undefined}
              disabled={!projectId}
            >
              <ListItemIcon title="List View">
                <ListAltIcon />
              </ListItemIcon>
              {open && <ListItemText primary="List View" />}
            </ListItem>
          </span>
        </Tooltip>
        */}

{(isAdmin || isSuperAdmin) && ( // Admin Tools section: Added Reports under Admin tools
  <>
        <Tooltip title={projectId && !isProjectPage ? '' : 'Select project first'} arrow>
          <span>
            <ListItem
              button
              component={projectId && !isProjectPage ? Link : 'div'}
              to={projectId && !isProjectPage ? `/sprints/${projectId}` : undefined}
              disabled={!projectId || isProjectPage}
            >
              <ListItemIcon title="Sprint">
                <DirectionsRunIcon />
              </ListItemIcon>
               {isExpanded && <ListItemText primary="Sprint" />}
            </ListItem>
          </span>
        </Tooltip>

        <Tooltip title={projectId && !isProjectPage ? '' : 'Select project first'} arrow>
          <span>
            <ListItem
              button
              component={projectId && !isProjectPage ? Link : 'div'}
              to={projectId && !isProjectPage ? `/milestone/${projectId}` : undefined}
              disabled={!projectId || isProjectPage}
            >
              <ListItemIcon title="Milestone">
                <FlagIcon />
              </ListItemIcon>
               {isExpanded && <ListItemText primary="Milestone" />}
            </ListItem>
          </span>
        </Tooltip>

    <ListItem button component={Link} to="/rules">
      <ListItemIcon>
        <RuleIcon />
      </ListItemIcon>
      {isExpanded && <ListItemText primary="Rules" />}
    </ListItem>

    <ListItem button component={Link} to="/user-list">
      <ListItemIcon>
        <GroupAddIcon />
      </ListItemIcon>
      {isExpanded && <ListItemText primary="User List" />}
    </ListItem>

    <ListItem button component={Link} to="/reports">
      <ListItemIcon>
        <BarChartIcon />
      </ListItemIcon>
      {isExpanded && <ListItemText primary="Reports" />}
    </ListItem>
  </>
)}

        {isAuthenticated ? (
          <ListItem button onClick={handleLogout}>
            <ListItemIcon title="Logout">
              <LogoutIcon />
            </ListItemIcon>
            {isExpanded && <ListItemText primary="Logout" />}
          </ListItem>
        ) : (
          <ListItem button component={Link} to="/login">
            <ListItemIcon title="Sign In">
              <ListAltIcon />
            </ListItemIcon>
            {isExpanded && <ListItemText primary="Sign In" />}
          </ListItem>

          
        )}
      </List>

    </Drawer>
  );
};

export default SideDrawer;
